import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, facilitiesAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowRight, Building2, Check, MapPin,
  Phone, Camera, Sparkles, Image as ImageIcon,
} from 'lucide-react';
import { PlacesSearch, PlaceCard } from '@/components/PlacesSearch';
import { ImageCropUpload } from '@/components/ImageCropUpload';
import { LocationPicker } from '@/components/FacilityMap';

const AMENITIES = [
  { key: 'lockers', label: 'Spogliatoi', icon: '🚿' },
  { key: 'showers', label: 'Docce', icon: '🛁' },
  { key: 'parking', label: 'Parcheggio', icon: '🅿️' },
  { key: 'wifi', label: 'Wi-Fi', icon: '📶' },
  { key: 'bar', label: 'Bar / Ristoro', icon: '🍻' },
  { key: 'first_aid', label: 'Primo soccorso', icon: '🚑' },
  { key: 'wheelchair_access', label: 'Accesso disabili', icon: '♿' },
  { key: 'equipment_rental', label: 'Noleggio attrezzatura', icon: '🎒' },
];

const DAYS = [
  { key: 'mon', label: 'Lunedì' },
  { key: 'tue', label: 'Martedì' },
  { key: 'wed', label: 'Mercoledì' },
  { key: 'thu', label: 'Giovedì' },
  { key: 'fri', label: 'Venerdì' },
  { key: 'sat', label: 'Sabato' },
  { key: 'sun', label: 'Domenica' },
];

const STEPS = [
  { n: 1, label: 'Struttura', icon: Building2 },
  { n: 2, label: 'Contatti', icon: Phone },
  { n: 3, label: 'Servizi & Orari', icon: Sparkles },
  { n: 4, label: 'Foto', icon: Camera },
];

// =============================================================================
// Stepper
// =============================================================================

const Stepper = ({ step, onJump }) => (
  <div className="flex min-w-0 items-center justify-between gap-1 mb-6">
    {STEPS.map((item, idx) => {
      const isDone = step > item.n;
      const isActive = step === item.n;
      const Icon = item.icon;
      return (
        <React.Fragment key={item.n}>
          <button
            type="button"
            onClick={() => onJump(item.n)}
            className="flex shrink-0 items-center gap-2"
          >
            <div
              className={`w-9 h-9 rounded-full flex items-center justify-center font-semibold text-sm shadow-sm transition-all ${
                isActive
                  ? 'bg-gradient-to-br from-emerald-500 to-blue-600 text-white scale-110'
                  : isDone
                    ? 'bg-emerald-500 text-white'
                    : 'bg-muted text-muted-foreground'
              }`}
            >
              {isDone ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
            </div>
            <span className={`text-sm font-medium hidden sm:inline ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}>
              {item.label}
            </span>
          </button>
          {idx < STEPS.length - 1 && (
            <div className={`min-w-2 flex-1 h-0.5 mx-1 sm:mx-2 ${step > item.n ? 'bg-emerald-300' : 'bg-muted'}`} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

// =============================================================================
// Live preview
// =============================================================================

const PreviewCard = ({ formData, selectedPlace }) => {
  const activeAmenities = useMemo(
    () => AMENITIES.filter((a) => formData.amenities[a.key]).slice(0, 4),
    [formData.amenities],
  );
  return (
    <div className="space-y-3">
      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
        Anteprima pagina pubblica
      </div>
      <Card className="overflow-hidden">
        <div className="h-32 bg-gradient-to-br from-emerald-400 to-blue-500 relative flex items-end p-3">
          {formData.cover_image_url ? (
            <img
              src={formData.cover_image_url}
              alt={formData.name}
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : null}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <div className="relative text-white">
            <div className="font-bold text-lg leading-tight line-clamp-2">
              {formData.name || 'Nome struttura'}
            </div>
            <div className="text-xs opacity-90 flex items-center gap-1 mt-0.5">
              <MapPin className="h-3 w-3" />
              {formData.city || (selectedPlace?.address?.split(',')[0]) || 'Città'}
            </div>
          </div>
        </div>
        <CardContent className="pt-3 space-y-2 text-sm">
          {formData.description && (
            <p className="text-muted-foreground line-clamp-3">{formData.description}</p>
          )}
          {activeAmenities.length > 0 && (
            <div className="flex gap-1 flex-wrap pt-1">
              {activeAmenities.map((a) => (
                <span key={a.key} className="px-1.5 py-0.5 bg-muted rounded text-xs">
                  {a.icon} {a.label}
                </span>
              ))}
            </div>
          )}
          {formData.phone && (
            <div className="text-xs text-muted-foreground">📞 {formData.phone}</div>
          )}
          {!formData.name && (
            <div className="text-xs text-muted-foreground italic">
              Compila i dati per vedere come appare ai giocatori
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// =============================================================================
// Main
// =============================================================================

export const CreateFacilityPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [selectedPlace, setSelectedPlace] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: '',
    city: '',
    lat: null,
    lng: null,
    map_url: '',
    phone: '',
    email: '',
    website: '',
    cover_image_url: '',
    logo_url: '',
    amenities: {},
    opening_hours: {},
  });

  useEffect(() => {
    if (isEditing) {
      const fetchFacility = async () => {
        try {
          const res = await facilitiesAPI.getById(id);
          const fac = res.data.facility;
          setFormData({
            name: fac.name || '',
            description: fac.description || '',
            address: fac.address || '',
            city: fac.city || '',
            lat: fac.lat,
            lng: fac.lng,
            map_url: fac.map_url || '',
            phone: fac.phone || '',
            email: fac.email || '',
            website: fac.website || '',
            cover_image_url: fac.cover_image_url || '',
            logo_url: fac.logo_url || '',
            amenities: fac.amenities || {},
            opening_hours: fac.opening_hours || {},
          });
          if (fac.address) {
            setSelectedPlace({ name: fac.name, address: fac.address, lat: fac.lat, lng: fac.lng, map_url: fac.map_url });
          }
        } catch (error) {
          toast.error('Errore nel caricamento');
          navigate('/facilities');
        }
      };
      fetchFacility();
    }
  }, [id, isEditing, navigate]);

  const handlePlaceSelect = (place) => {
    setSelectedPlace(place);
    setFormData((prev) => ({
      ...prev,
      name: prev.name.trim() || place.name,
      address: place.address,
      city: place.city || prev.city,
      lat: place.lat,
      lng: place.lng,
      map_url: place.map_url || `https://www.google.com/maps?q=${place.lat},${place.lng}`,
    }));
  };

  const handlePinMove = async ({ lat, lng }) => {
    const map_url = `https://www.google.com/maps?q=${lat},${lng}`;
    setFormData((prev) => ({ ...prev, lat, lng, map_url }));
    setSelectedPlace((prev) => ({ ...prev, lat, lng, map_url }));
    try {
      const res = await api.get('/places/reverse-geocode', { params: { lat, lng } });
      if (!res.data?.address) return;
      setFormData((prev) => ({ ...prev, address: res.data.address, city: res.data.city || prev.city }));
      setSelectedPlace((prev) => ({ ...prev, address: res.data.address }));
    } catch {
      // ponytail: the pin alone is enough, the address is a nicety
    }
  };

  const toggleAmenity = (key) => {
    setFormData((prev) => ({
      ...prev,
      amenities: { ...prev.amenities, [key]: !prev.amenities[key] },
    }));
  };

  const updateOpeningHour = (day, field, value) => {
    setFormData((prev) => ({
      ...prev,
      opening_hours: {
        ...prev.opening_hours,
        [day]: { ...(prev.opening_hours[day] || {}), [field]: value },
      },
    }));
  };

  const toggleDayClosed = (day) => {
    setFormData((prev) => {
      const current = prev.opening_hours[day];
      if (current?.closed) {
        return { ...prev, opening_hours: { ...prev.opening_hours, [day]: { open: '08:00', close: '23:00' } } };
      }
      return { ...prev, opening_hours: { ...prev.opening_hours, [day]: { closed: true } } };
    });
  };

  const validateStep = (s) => {
    if (s === 1 && !formData.name.trim()) {
      toast.error('Inserisci il nome della struttura');
      return false;
    }
    if (s === 1 && !formData.address && formData.lat == null) {
      toast.error('Cerca l\'indirizzo o tocca la mappa dove si trova la struttura');
      return false;
    }
    return true;
  };

  const goNext = () => {
    if (!validateStep(step)) return;
    setStep((s) => Math.min(STEPS.length, s + 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Inserisci il nome della struttura');
      setStep(1);
      return;
    }
    setLoading(true);
    try {
      if (isEditing) {
        await facilitiesAPI.update(id, formData);
        toast.success('Struttura aggiornata!');
        navigate(`/facilities/${id}/dashboard`);
      } else {
        const res = await facilitiesAPI.create(formData);
        toast.success('🎉 Struttura registrata!');
        // Suggest next step: add fields
        navigate(`/facilities/${res.data.facility_id}/dashboard#fields`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-w-0 overflow-x-hidden p-3 sm:p-4 md:p-6 lg:p-8 max-w-6xl mx-auto" data-testid="create-facility-page">
      <Button variant="ghost" className="mb-4" onClick={() => navigate(-1)}>
        <ArrowLeft className="h-4 w-4 mr-2" />
        Indietro
      </Button>

      <div className="mb-4 flex min-w-0 items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-blue-600 flex items-center justify-center text-white shadow">
          <Building2 className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <h1 className="text-2xl font-bold leading-tight">
            {isEditing ? 'Modifica struttura' : 'Registra la tua struttura'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isEditing ? 'Aggiorna i dati e salva' : '4 step e sei online sul marketplace'}
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 min-w-0">
          <Card className="min-w-0 overflow-hidden">
            <CardContent className="p-3 pt-5 sm:p-6 sm:pt-6">
              <Stepper step={step} onJump={(n) => setStep(n)} />

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* STEP 1 — Struttura */}
                {step === 1 && (
                  <div className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="name">Nome struttura *</Label>
                      <Input
                        id="name"
                        placeholder="Es: Centro Sportivo Roma Nord (oppure cercalo qui sotto)"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        data-testid="facility-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="description">Descrizione</Label>
                      <Textarea
                        id="description"
                        placeholder="Es: 3 campi outdoor + 2 indoor coperti, parcheggio gratuito, bar..."
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        rows={3}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Dove si trova *</Label>
                      <PlacesSearch
                        onSelect={handlePlaceSelect}
                        placeholder="Cerca centro sportivo, campo o indirizzo..."
                        initialValue={formData.address}
                      />
                      <LocationPicker
                        lat={formData.lat}
                        lng={formData.lng}
                        onChange={handlePinMove}
                        className="h-64 sm:h-72 w-full rounded-xl overflow-hidden border"
                      />
                      <p className="text-xs text-muted-foreground">
                        Cerca l'indirizzo oppure tocca la mappa, poi trascina il segnaposto sopra i campi:
                        è lì che i giocatori ti troveranno.
                      </p>
                      {selectedPlace?.address && (
                        <PlaceCard
                          name={selectedPlace.name || formData.name}
                          address={selectedPlace.address}
                          lat={selectedPlace.lat}
                          lng={selectedPlace.lng}
                          mapUrl={selectedPlace.map_url}
                        />
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="city">Città</Label>
                      <Input
                        id="city"
                        placeholder="Es: Roma"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      />
                    </div>
                  </div>
                )}

                {/* STEP 2 — Contatti */}
                {step === 2 && (
                  <div className="space-y-5">
                    <p className="text-sm text-muted-foreground">
                      Questi contatti saranno visibili sulla pagina pubblica della struttura.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="phone">Telefono</Label>
                        <Input
                          id="phone"
                          placeholder="+39 ..."
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="info@struttura.it"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="website">Sito Web</Label>
                      <Input
                        id="website"
                        placeholder="https://..."
                        value={formData.website}
                        onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      />
                    </div>
                  </div>
                )}

                {/* STEP 3 — Servizi & Orari */}
                {step === 3 && (
                  <div className="space-y-6">
                    <div>
                      <Label className="text-base font-semibold mb-3 block">Servizi disponibili</Label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {AMENITIES.map((amenity) => {
                          const active = !!formData.amenities[amenity.key];
                          return (
                            <button
                              key={amenity.key}
                              type="button"
                              onClick={() => toggleAmenity(amenity.key)}
                              data-testid={`amenity-${amenity.key}`}
                              className={`p-3 rounded-lg border flex items-center justify-between transition ${
                                active
                                  ? 'border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-900'
                                  : 'border-border hover:bg-muted'
                              }`}
                            >
                              <span className="text-sm font-medium flex items-center gap-2">
                                <span className="text-lg">{amenity.icon}</span>
                                {amenity.label}
                              </span>
                              {active && <Check className="h-4 w-4 text-emerald-600" />}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <Label className="text-base font-semibold mb-3 block">Orari di apertura</Label>
                      <div className="space-y-2">
                        {DAYS.map((day) => {
                          const hours = formData.opening_hours[day.key] || {};
                          const isClosed = hours.closed;
                          return (
                            <div key={day.key} className="flex flex-wrap items-center gap-2 p-2 rounded-lg border">
                              <span className="w-24 text-sm font-medium">{day.label}</span>
                              <Button
                                type="button"
                                variant={isClosed ? 'destructive' : 'outline'}
                                size="sm"
                                onClick={() => toggleDayClosed(day.key)}
                                className="text-xs w-20"
                              >
                                {isClosed ? 'Chiuso' : 'Aperto'}
                              </Button>
                              {!isClosed && (
                                <>
                                  <Input
                                    type="time"
                                    value={hours.open || '08:00'}
                                    onChange={(e) => updateOpeningHour(day.key, 'open', e.target.value)}
                                    className="w-28"
                                  />
                                  <span className="text-muted-foreground">—</span>
                                  <Input
                                    type="time"
                                    value={hours.close || '23:00'}
                                    onChange={(e) => updateOpeningHour(day.key, 'close', e.target.value)}
                                    className="w-28"
                                  />
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* STEP 4 — Foto */}
                {step === 4 && (
                  <div className="space-y-5">
                    <div>
                      <Label className="text-base font-semibold mb-2 flex items-center gap-2">
                        <ImageIcon className="h-4 w-4" />
                        Immagine di copertina
                      </Label>
                      <p className="text-sm text-muted-foreground mb-3">
                        È la prima cosa che vedono i giocatori. Foto in HD del campo / ingresso.
                      </p>
                      <ImageCropUpload
                        value={formData.cover_image_url}
                        onChange={(url) => setFormData({ ...formData, cover_image_url: url })}
                        preset="cover"
                      />
                    </div>

                    {!isEditing && (
                      <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-900 text-sm">
                        <div className="font-semibold text-emerald-900 dark:text-emerald-200 mb-1 flex items-center gap-1.5">
                          <Sparkles className="h-4 w-4" />
                          Sei quasi pronto!
                        </div>
                        <p className="text-emerald-800 dark:text-emerald-300">
                          Dopo il salvataggio aggiungi i tuoi <strong>campi</strong> (calcetto, padel...)
                          e i <strong>prezzi</strong> per slot orario. Da quel momento i giocatori potranno
                          prenotare direttamente.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Navigation */}
                <div className="flex gap-3 pt-4 border-t">
                  {step > 1 ? (
                    <Button type="button" variant="outline" onClick={() => setStep((s) => s - 1)} className="flex-1 sm:flex-initial">
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      Indietro
                    </Button>
                  ) : (
                    <Button type="button" variant="outline" onClick={() => navigate(-1)} className="flex-1 sm:flex-initial">
                      Annulla
                    </Button>
                  )}

                  {step < STEPS.length ? (
                    <Button
                      type="button"
                      onClick={goNext}
                      className="flex-1 bg-gradient-to-br from-emerald-500 to-blue-600 hover:opacity-90"
                    >
                      Avanti
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  ) : (
                    <Button
                      type="submit"
                      disabled={loading}
                      className="flex-1 bg-gradient-to-br from-emerald-500 to-emerald-600 hover:opacity-90"
                      data-testid="submit-facility-btn"
                    >
                      {loading
                        ? (isEditing ? 'Salvataggio…' : 'Registrazione…')
                        : (isEditing ? 'Salva modifiche' : 'Registra struttura')}
                      <Check className="h-4 w-4 ml-2" />
                    </Button>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Preview */}
        <aside className="lg:col-span-1">
          <div className="lg:sticky lg:top-6">
            <PreviewCard formData={formData} selectedPlace={selectedPlace} />
          </div>
        </aside>
      </div>
    </div>
  );
};

export default CreateFacilityPage;
